import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import signalRService, { ConnectionStatus, ConnectionStatusInfo } from '../services/signalr';

interface ConnectionStatusContextValue {
  status: ConnectionStatus;
  info: ConnectionStatusInfo;
  /** Bottone "Riprova ora" del banner offline. */
  forceReconnect: () => Promise<void>;
}

const ConnectionStatusContext = createContext<ConnectionStatusContextValue>({
  status: 'disconnected',
  info: { changedAt: Date.now(), lastSuccessfulPingAt: null, restartAttempt: 0 },
  forceReconnect: async () => {},
});

/**
 * Provider che si iscrive al singleton SignalRService e propaga lo stato della
 * connessione in tutta la UI. Risolve il problema "client freezzato senza che
 * l'utente se ne accorga": il chip nell'header e il banner offline derivano da
 * questo context, garantendo feedback visivo immediato in caso di problemi.
 *
 * Il context usa un singolo subscribe sul service; tutti i consumer ricevono
 * gli update via React state — niente polling locale.
 */
export const ConnectionStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<ConnectionStatus>(signalRService.getConnectionStatus());
  const [info, setInfo] = useState<ConnectionStatusInfo>(signalRService.getConnectionInfo());

  useEffect(() => {
    const unsubscribe = signalRService.onConnectionStatusChange((s, i) => {
      setStatus(s);
      if (i) setInfo(i);
    });
    return unsubscribe;
  }, []);

  // Tick ogni 2s per aggiornare il "tempo offline" mostrato nel banner senza
  // dover dipendere da nuovi eventi. È un setInterval molto leggero perché
  // setInfo crea un nuovo oggetto solo se serve mostrare un counter live.
  useEffect(() => {
    if (status === 'connected') return; // niente tick quando tutto va bene
    const t = setInterval(() => {
      setInfo({ ...signalRService.getConnectionInfo() });
    }, 2000);
    return () => clearInterval(t);
  }, [status]);

  const forceReconnect = useCallback(async () => {
    await signalRService.forceReconnect();
  }, []);

  return (
    <ConnectionStatusContext.Provider value={{ status, info, forceReconnect }}>
      {children}
    </ConnectionStatusContext.Provider>
  );
};

export const useConnectionStatus = () => useContext(ConnectionStatusContext);
